import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import FlightBookingProgressSheet, {
  FlightBookingProgressSummary,
} from '../components/chat/sidebar/FlightBookingProgressSheet';
import {
  useFlightArtifactFetcher,
  useFlightBookingState,
} from '../components/chat/sidebar/useFlightBookingState';
import {
  FlightArtifactBookingState,
  isSettledFlightBookingState,
} from '../components/chat/sidebar/flightArtifact';

/**
 * States worth remembering for the artifact view: a press parked with ops
 * (awaiting_payment) and how a press ended. `booking` is transient and the
 * sheet already shows it.
 */
const remembered = (state: FlightArtifactBookingState): boolean =>
  state.state === 'awaiting_payment' || isSettledFlightBookingState(state);

export interface FlightBookingProgressRequest {
  artifactId: string;
  shareToken?: string;
  summary: FlightBookingProgressSummary;
  assistantName: string;
}

interface FlightBookingProgressContextType {
  /** Opens the progress sheet for a Book press that has just been accepted. */
  start: (request: FlightBookingProgressRequest) => void;
  /** Closes it early (e.g. the Book request itself failed). */
  abort: (artifactId: string) => void;
  /** The artifact whose Book press is still being settled, if any. */
  pendingArtifactId: string | null;
  /**
   * The latest state of the last Book press on an artifact: awaiting_payment
   * while ops collect a saved-card payment, then how it ended.
   */
  stateFor: (artifactId: string) => FlightArtifactBookingState | null;
}

interface ActiveBooking {
  request: FlightBookingProgressRequest;
  /** Set once the press has settled; the sheet stays up until dismissed. */
  outcome: FlightArtifactBookingState | null;
}

const FlightBookingProgressContext =
  createContext<FlightBookingProgressContextType | null>(null);

/** Null outside the provider; callers then render the sheet themselves. */
export const useOptionalFlightBookingProgress =
  (): FlightBookingProgressContextType | null =>
    useContext(FlightBookingProgressContext);

const ActiveProgress: React.FC<{
  request: FlightBookingProgressRequest;
  onState: (artifactId: string, state: FlightArtifactBookingState) => void;
  onDone: () => void;
}> = ({ request, onState, onDone }) => {
  const fetchSnapshot = useFlightArtifactFetcher(
    request.artifactId,
    request.shareToken,
  );
  const state = useFlightBookingState(request.artifactId, true, fetchSnapshot);
  useEffect(() => {
    if (state && remembered(state)) {
      onState(request.artifactId, state);
    }
  }, [state, request.artifactId, onState]);
  return (
    <FlightBookingProgressSheet
      show
      state={state}
      summary={request.summary}
      assistantName={request.assistantName}
      notifyVia={request.shareToken ? 'whatsapp' : 'chat'}
      onDone={onDone}
    />
  );
};

/**
 * Hosts the flight booking progress sheet above the artifact modals, so it
 * survives the host closing the modal once the Book request is accepted.
 */
export const FlightBookingProgressProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [active, setActive] = useState<ActiveBooking | null>(null);
  // Where earlier presses got to, so an artifact reopened in the same session
  // still knows it is parked with ops or can be retried. An aborted press
  // (request never queued) leaves the previous state in place.
  const [states, setStates] = useState<
    Record<string, FlightArtifactBookingState>
  >({});
  const start = useCallback(
    (request: FlightBookingProgressRequest) =>
      setActive({ request, outcome: null }),
    [],
  );
  const abort = useCallback(
    (artifactId: string) =>
      setActive((current) =>
        current?.request.artifactId === artifactId ? null : current,
      ),
    [],
  );
  const record = useCallback(
    (artifactId: string, state: FlightArtifactBookingState) => {
      if (isSettledFlightBookingState(state)) {
        setActive((current) =>
          current?.request.artifactId === artifactId
            ? { ...current, outcome: state }
            : current,
        );
      }
      setStates((prev) =>
        prev[artifactId] === state ? prev : { ...prev, [artifactId]: state },
      );
    },
    [],
  );
  const pendingArtifactId =
    active && !active.outcome ? active.request.artifactId : null;
  const value = useMemo(
    () => ({
      start,
      abort,
      pendingArtifactId,
      stateFor: (artifactId: string) => states[artifactId] ?? null,
    }),
    [start, abort, pendingArtifactId, states],
  );
  return (
    <FlightBookingProgressContext.Provider value={value}>
      {children}
      {active && (
        <ActiveProgress
          key={active.request.artifactId}
          request={active.request}
          onState={record}
          onDone={() => setActive(null)}
        />
      )}
    </FlightBookingProgressContext.Provider>
  );
};
